import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Loader2, Check, XCircle } from "lucide-react";

import { Button } from "../ui/button";
import AiCopilot from "../AiCopilot";
import TicketNotes from "../TicketNotes";
import CallButton from "../CallButton";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

/**
 * @param {Object} props
 * @param {Object} props.ticket - ticket no state da página.
 * @param {(atualizado: Object) => void} [props.onTicketChanged] - callback
 *        opcional chamado quando o ticket em tela deve ser substituído por
 *        uma versão nova (ex.: menção dispensada). Se não vier, o
 *        TicketActionButtons confia só no evento socket emitido pelo backend
 *        que o ChatView /componente Ticket já ouve.
 */
const TicketActionButtons = ({ ticket, onTicketChanged }) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const { whatsApps, loading: loadingWhatsapps } = useWhatsApps();

	const slaCountGroupsMap = useMemo(() => {
		const m = new Map();
		(Array.isArray(whatsApps) ? whatsApps : []).forEach((w) => {
			if (!w || w.id == null) return;
			m.set(Number(w.id), Boolean(w.slaCountGroups));
		});
		return m;
	}, [whatsApps]);

	const ticketEhGrupo = String(ticket?.isGroup) === "true";
	const conexaoId = Number((ticket?.whatsappId ?? ticket?.whatsapp?.id) || 0);
	const conexaoTemSlaGrupos = conexaoId ? slaCountGroupsMap.get(conexaoId) : undefined;
	const esconderAcoesGrupoSemSla =
		ticketEhGrupo === true &&
		(loadingWhatsapps === true ||
			conexaoTemSlaGrupos === false ||
			(conexaoId && conexaoTemSlaGrupos === undefined));

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				navigate(`/tickets/${ticket.id}`);
			} else {
				navigate("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	// Quando o usuário logado foi MENCIONADO neste ticket (nota privada
	// com @Nome dele) e ainda NÃO É o dono do ticket e NÃO dispensou a
	// menção, mostramos as ações de menção no cabeçalho.
	const temMencaoParaMimEnaoSouDono =
		Boolean(ticket?.hasMentionForUser || Number(ticket?.unreadMentions) > 0) &&
		Number(ticket?.userId) !== Number(user?.id);

	// Dispensa a menção do usuário logado no ticket atual. Chama o
	// endpoint /tickets/:id/dismiss-mention que marca seenAt e dismissedAt
	// de todas as menções pendentes dele neste ticket. O resultado:
	//   - badge "Mensagem privada" some na lista
	//   - o ticket some da lista de "menções novas" do usuário (se ele não
	//     for dono)
	//   - hasMentionForUser vai para false -> botões desaparecem e o
	//     MessageInput desbloqueia o switch de modo privado (se era
	//     mencionado não dono).
	// NÃO TOCA em atribuição (userId) nem status do atendimento: "Aceitar
	// a menção" aqui é só confirmar "vi e estou ciente"; não vira dono.
	const dispensarMencao = async () => {
		setLoading(true);
		try {
			const { data: ticketAtualizado } = await api.post(
				`/tickets/${ticket.id}/dismiss-mention`,
				{}
			);
			if (typeof onTicketChanged === "function" && ticketAtualizado) {
				onTicketChanged(ticketAtualizado);
			}
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	// Botão "Aceitar conversa" (no cabeçalho) NÃO TRANSFERE mais o ticket
	// para o atendente mencionado. Apenas dispensa a menção, igual o botão
	// Fechar mensagem privada. Usuário preferiu: "aceitar" no contexto da
	// MENÇÃO, não no contexto de dono do atendimento.
	const aceitarMencaoSemTransferir = async () => {
		await dispensarMencao();
	};

	return (
		<div className="ml-auto flex shrink-0 items-center gap-1.5">
			{/* Se o usuário foi mencionado e ainda não é dono: mostra estes
			    dois botões de menção ANTES de CallButton/Notas para chamar
			    atenção. São as ações mais importantes do momento. */}
			{temMencaoParaMimEnaoSouDono && !esconderAcoesGrupoSemSla && (
				<>
					<Button
						size="sm"
						variant="secondary"
						disabled={loading}
						className="border border-emerald-300/70 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700/50 dark:hover:bg-emerald-900/40"
						onClick={aceitarMencaoSemTransferir}
					>
						{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
						Aceitar conversa
					</Button>
					<Button
						size="sm"
						variant="outline"
						disabled={loading}
						onClick={dispensarMencao}
					>
						{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
						Fechar mensagem privada
					</Button>
				</>
			)}

			<CallButton ticket={ticket} />
			<TicketNotes ticketId={ticket.id} />
			<AiCopilot
				ticketId={ticket.id}
				onUsarTexto={(texto) =>
					document.dispatchEvent(
						new CustomEvent("copilot:usar-texto", { detail: texto })
					)
				}
			/>
			{/* O menu sai de dentro do bloco de "open": pausar o robô e ajustar a
			    automação valem em qualquer situação, e é justamente na conversa
			    ainda AGUARDANDO que se quer calar o robô — antes de assumir. */}
			<TicketOptionsMenu ticket={ticket} />
			{ticket.status === "closed" && (
				<Button
					size="sm"
					variant="outline"
					disabled={loading}
					onClick={(e) => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
					{i18n.t("messagesList.header.buttons.reopen")}
				</Button>
			)}
			{ticket.status === "open" && (
				<>
					<Button
						size="sm"
						variant="outline"
						disabled={loading}
						onClick={(e) => handleUpdateTicketStatus(e, "pending", null)}
					>
						{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
						{i18n.t("messagesList.header.buttons.return")}
					</Button>
					<Button
						size="sm"
						disabled={loading}
						onClick={(e) => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{loading && <Loader2 className="h-4 w-4 animate-spin" />}
						{i18n.t("messagesList.header.buttons.resolve")}
					</Button>
				</>
			)}
			{ticket.status === "pending" && !temMencaoParaMimEnaoSouDono && !esconderAcoesGrupoSemSla && (
				<Button
					size="sm"
					disabled={loading}
					onClick={(e) => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{loading && <Loader2 className="h-4 w-4 animate-spin" />}
					{i18n.t("messagesList.header.buttons.accept")}
				</Button>
			)}
		</div>
	);
};

export default TicketActionButtons;
